import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ChoiceProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoicePreviewSendOptionSectionProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { StyleEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { useCustomerService, useInvoiceService } from "serviceHooks";
import SendMailFormFields from "../_fields/sendMailFormFields";
import { GroupSection, VerticalFormField, Choice } from "components/common/wintForms";
import useInvoiceAlerts from "../../alerts/useAlerts";
import SendInformationLoader from "../preview/sendInformationLoader";
import usePrintAllowed from "./useSendOptionProperties";
import ForwardedCostWarnings from "./forwardedCostWarnings";
import useGetMailOptionDefaults from "./useGetMailOptionDefaults";
import { ActionLinkButton, Icon, StaticInfoMessage } from "components/common";
import { useReferencePersonDialog } from "components/customer";
import ReferenceCodeCaption from "../_fields/referenceCodeCaption";
import { Grid } from "@mui/material";
//#endregion
import { EnumHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const InvoicePreviewSendOptionSection: React.FC<InvoicePreviewSendOptionSectionProps> = (props) => {
  const invoiceService = useInvoiceService();
  const customerService = useCustomerService();

  const {
    values,
    connect,
    setValues,
    canSendLastVersionToPagero,
    invoiceId,
    invoicePut,
    version,
    isReminder,
    status,
    warnings: invoiceWarnings,
    noGrouping,
    onInvoiceSendMethodSelected,
    onWarningsChange,
  } = props;

  const [warnings, setWarnings] = React.useState(invoiceWarnings);
  // undefined = the customer's reference persons haven't been loaded yet. Distinct from [] (loaded, none),
  // because "not loaded yet" must not be rendered as "this person has no reference code".
  const [referencePersons, setReferencePersons] = React.useState<WintSuperkollAPIModel.Reference[]>();

  const isRegisteredAtPagero = useSelector((store: AppState) => store.auth.selectedCompany.isRegisteredAtPagero);
  const pageroEInvoice = useSelector((store: AppState) => store.auth.selectedCompany.pageroEInvoice);

  const { showWarnings } = useInvoiceAlerts();
  const { getEmailToDefault } = useGetMailOptionDefaults();
  const { showReferencePersonDialog } = useReferencePersonDialog();

  const sendTypeProps = connect("sendType");

  // "values" is a Preview/Todo union whose reference-person fields don't share a shape (raw ids on Preview,
  // an AutocompleteListItem on Todo) — read both via a cast rather than relying on "in"-narrowing across the union.
  const previewValues = values as InvoiceFormTypes.Preview;
  const todoValues = values as InvoiceFormTypes.Todo;
  const customerId = previewValues.customerId ?? (todoValues.customer?.id as number | undefined);
  const referencePersonId = previewValues.referencePersonId ?? (todoValues.referencePerson?.id as number | undefined);
  // The server raises the missing-code warning even when no reference was entered at all; its own text
  // ("the customer reference you've entered") would then be wrong.
  const hasNoReference =
    !referencePersonId && !(previewValues.referencePersonName ?? todoValues.referencePerson?.value);

  const missingReferenceCodeWarning = warnings?.find(
    (w) => w.type === WintSuperkollAPIModel.WarningType.MissingCustomerReferenceCode
  );
  const missingElectronicIdentifiersWarning = warnings?.find(
    (w) => w.type === WintSuperkollAPIModel.WarningType.MissingCustomerElectronicIdentifiers
  );
  const sendAsEInvoiceWarning = warnings?.find((w) => w.type === WintSuperkollAPIModel.WarningType.SendAsEInvoice);
  // The more blocking warning wins when both apply — never stack more than one banner per section.
  const bannerWarning = missingElectronicIdentifiersWarning ?? missingReferenceCodeWarning;

  const isMissingReferenceCodeWarning =
    bannerWarning?.type === WintSuperkollAPIModel.WarningType.MissingCustomerReferenceCode;

  // Nothing below renders a reference person unless the company can send e-invoices or one of the two
  // reference-related warnings is up, so companies that can never see the row shouldn't pay for the fetch.
  const needsReferencePersons = !!pageroEInvoice || !!bannerWarning;

  React.useEffect(() => {
    onWarningsChange?.(warnings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warnings]);

  React.useEffect(() => {
    if (values.sendType !== undefined && warnings?.length) {
      showWarnings(status ?? WintSuperkollAPIModel.InvoiceStatus.NotSent, values.sendType, warnings);
    }

    onInvoiceSendMethodSelected && onInvoiceSendMethodSelected(values.sendType);
  }, [warnings, values.sendType]);

  React.useEffect(() => {
    if (invoicePut) {
      invoiceService
        .getInvoiceWarnings(invoicePut, { id: invoiceId, status })
        .then((res) => {
          setWarnings(res);
        })
        // Keep the warnings we were handed by the parent rather than rejecting unhandled.
        .catch(() => undefined);
    }
  }, []);

  const loadReferencePersons = (id: number, isStale: () => boolean = () => false) =>
    customerService
      .getCustomer(id)
      .then((customer) => {
        if (!isStale()) setReferencePersons(customer?.referencePersonsList ?? []);
      })
      .catch(() => undefined);

  React.useEffect(() => {
    if (!customerId || !needsReferencePersons) return;
    let cancelled = false;
    loadReferencePersons(customerId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [customerId, needsReferencePersons]);

  // "connect" is a union across Preview/Todo, whose reference-person fields don't share a name — narrow with a
  // minimal local cast rather than threading the whole union through WintFormConnect's generic call signature.
  const connectField = connect as unknown as (name: string) => { value: any; setValue: (value: any) => void };

  const assignReferencePerson = (person: { id?: number; name?: string }) => {
    if (previewValues.customerId !== undefined) {
      connectField("referencePersonId").setValue(person.id);
      // The name travels with the id: it's what ends up in the invoice's customerReference when the
      // preview is saved, and preview.tsx has no reference-person list of its own to look it up in.
      connectField("referencePersonName").setValue(person.name);
    } else {
      connectField("referencePerson").setValue(person.id ? { id: person.id, value: person.name ?? "" } : undefined);
    }
  };

  // SendInformationLoader is what normally fills the mail fields, but it runs on [invoiceId, version] only
  // and returns early once mailOptions exist. Saving a reference person changes neither, so without this
  // the recipient would stay blank or keep pointing at the person selected before the dialog was opened.
  const applySavedReferencePersonToMailOptions = (email?: string) => {
    const customerEmail = values.mailOptions?.defaultEmailTo.customer;
    const emailToSettings = previewValues.defaultEmailToSettings;
    const previousDefault = getEmailToDefault(
      values.mailOptions?.defaultEmailTo.referencePerson,
      customerEmail,
      emailToSettings
    );
    const nextDefault = getEmailToDefault(email, customerEmail, emailToSettings);

    connectField("mailOptions.defaultEmailTo.referencePerson").setValue(email);

    if (email) {
      const mailToListProps = connectField("mailOptions.mailToList");
      if (!mailToListProps.value?.some((item: AutocompleteListItem) => item.value === email)) {
        mailToListProps.setValue([...(mailToListProps.value ?? []), { id: email, value: email }]);
      }
    }

    // Re-point the recipient only while it is still the default one — a recipient the user typed or
    // picked by hand is theirs to keep.
    const emailToProps = connectField("sendMail.emailTo");
    const currentEmails = (emailToProps.value ?? []).map((item: AutocompleteListItem) => item.value);
    if (!UtilsHelper.areStringArraysEqual(UtilsHelper.splitMultipleEmails(previousDefault ?? ""), currentEmails)) {
      return;
    }

    emailToProps.setValue(
      UtilsHelper.splitMultipleEmails(nextDefault ?? "").map((x: string): AutocompleteListItem => ({ id: x, value: x }))
    );
  };

  const refetchAfterReferencePersonSave = (savedPerson?: { id?: number; name?: string }) => {
    if (customerId) {
      loadReferencePersons(customerId);
    }
    if (invoicePut) {
      // The reference person the dialog just saved hasn't reached "invoicePut" yet — it is memoized
      // from the form values in the todo flow and an entirely static snapshot in the preview flow.
      // Asking for warnings against the stale put would re-report the code we just added as missing.
      // The form values now carry the person into the payload that is actually sent (preview.tsx and
      // convertTodoFormToAPIRequest), so this patched put mirrors what will go out.
      const put =
        savedPerson?.id !== undefined
          ? { ...invoicePut, customerReference: { referenceId: savedPerson.id, referenceName: savedPerson.name } }
          : invoicePut;

      invoiceService
        .getInvoiceWarnings(put, { id: invoiceId, status })
        .then(setWarnings)
        .catch(() => undefined);
    }
  };

  const { getPrintAllowed } = usePrintAllowed();
  const printAllowed = getPrintAllowed(
    invoiceId,
    canSendLastVersionToPagero ?? false,
    isRegisteredAtPagero,
    version,
    isReminder ?? false,
    status
  );

  const sendTypeItems: SelectionItem[] = React.useMemo(() => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendMethod)
      .filter((x) => printAllowed || x !== WintSuperkollAPIModel.InvoiceSendMethod.Print)
      .map((key) => {
        return {
          id: Number(key),
          value: invoiceService.getSendAsTypeText(key),
        };
      });
  }, []);

  const sendTypeChoiceComponent = () => (
    <VerticalFormField<ChoiceProps>
      Component={Choice}
      {...sendTypeProps}
      label={" "}
      showMainLabel={false}
      items={sendTypeItems}
    />
  );

  const selectedReferencePerson = referencePersonId
    ? referencePersons?.find((person) => person.id === referencePersonId)
    : undefined;
  const referenceCode = selectedReferencePerson?.referenceIdentifier;
  // The dialog saves the whole reference person from these props, so opening it before the customer's
  // list has loaded (or when the invoice points at a person missing from it) would either create a
  // duplicate person or PUT blank name/email/phone/isDefault over the real one. Only offer the action
  // once we can resolve what it would act on: a known person (edit) or no person at all (add).
  const canEditReferencePerson = referencePersons !== undefined && (!referencePersonId || !!selectedReferencePerson);

  const openReferencePersonDialog = () =>
    showReferencePersonDialog({
      customerId,
      person: selectedReferencePerson,
      onSaved: (submittedValues) => {
        // Also on edit: a renamed person must carry its new name into customerReference.
        assignReferencePerson({ id: submittedValues.id ?? referencePersonId, name: submittedValues.name });
        applySavedReferencePersonToMailOptions(submittedValues.email);
        refetchAfterReferencePersonSave({
          id: submittedValues.id ?? referencePersonId,
          name: submittedValues.name,
        });
      },
    });

  const renderReferenceCodeContent = () => (
    <Grid container direction="column" rowGap={2} sx={{ marginTop: "4px", paddingBottom: noGrouping ? "20px" : 0 }}>
      {sendAsEInvoiceWarning && (
        <Grid item className={clsx(cssExports.formControlDescription, cssExports.verticalFormDescription)}>
          {sendAsEInvoiceWarning.message}
        </Grid>
      )}
      {bannerWarning && (
        <Grid item>
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
            {isMissingReferenceCodeWarning ? (
              <Grid container direction="column" rowGap={1}>
                <Grid item>
                  {/* The name-bearing copy reads "the customer reference you entered ()" with no name,
                      which is what we'd render until the customer loads — fall back to the warning's
                      own message for as long as the name is unknown. */}
                  {hasNoReference
                    ? LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Invoice.MissingReferenceCodeNoPersonBannerText
                      )
                    : selectedReferencePerson?.name
                      ? LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Invoice.MissingReferenceCodeBannerText,
                          "",
                          {
                            name: selectedReferencePerson.name,
                          }
                        )
                      : (bannerWarning.message ?? "")}
                </Grid>
                {/* No reference: design wants the text alone, pointing back to edit mode — no action here. */}
                {canEditReferencePerson && !hasNoReference && (
                  <Grid item>
                    <ActionLinkButton className={cssExports.animatedLink} onClick={openReferencePersonDialog}>
                      {LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Invoice.MissingReferenceCodeBannerLink
                      )}
                      <Icon icon={IconDefinitionConstants.WArrowSmallRight} />
                    </ActionLinkButton>
                  </Grid>
                )}
              </Grid>
            ) : (
              (bannerWarning.message ?? "")
            )}
          </StaticInfoMessage>
        </Grid>
      )}
      {/* Suppressed whenever a banner is up, not only when it is the reference-code one. With both
          warnings present the identifiers banner wins, and letting the row through underneath it would
          put two calls to action for two different problems in the box — exactly what the
          one-banner-per-section rule above exists to prevent. */}
      {pageroEInvoice && !bannerWarning && canEditReferencePerson && (
        <Grid item>
          <ReferenceCodeCaption
            code={referenceCode}
            hasPerson={!!selectedReferencePerson}
            onLinkClick={openReferencePersonDialog}
          />
        </Grid>
      )}
    </Grid>
  );

  return (
    <>
      {noGrouping ? (
        <>
          {sendTypeChoiceComponent()}
          {values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.Print && renderReferenceCodeContent()}
        </>
      ) : (
        <>
          <ForwardedCostWarnings warnings={warnings} />
          <GroupSection header={TranslationKeys.Invoice.SendAs}>
            {sendTypeChoiceComponent()}
            {values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.Print && renderReferenceCodeContent()}
          </GroupSection>
        </>
      )}
      <SendInformationLoader
        invoiceId={invoiceId}
        values={values}
        version={version}
        isReminder={isReminder}
        setValues={setValues}
        connect={connect}
        nextInvoiceSerialNumber={props.nextInvoiceSerialNumber}
        skipSendInformationLoad={props.skipSendInformationLoad}
      />
      {values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.Email && (
        <SendMailFormFields
          connect={connect}
          invoiceId={invoiceId}
          mailOptions={values.mailOptions}
          parentFieldName="sendMail"
          fieldsLoading={values.loadingMailOptions}
          vertical
        />
      )}
    </>
  );
};

export default InvoicePreviewSendOptionSection;
